import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import enviro from 'enviro';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useRecaptchav3 } from 'developer-experience-shared-components/recaptcha/hooks/useRecaptchaV3';
import { authorize } from '../actions/AppActions';
import { V3_SITE_KEYS } from '../constants/Recaptcha';
import { getApplicationAuthorizationData, getAuthorizeStatus, getIsRequestingNewScopes } from '../selectors/AppSelectors';
import { getAuthorizationRequestInfo } from '../utils/urls';
import EmbeddedContext from '../utils/EmbeddedContext';
import { useFetchCurrentAccount } from '../hooks/queries/accounts/useFetchCurrentAccount';
import { jsx as _jsx } from "react/jsx-runtime";
const DisabledButtonTooltip = ({
  hasNoInstallPermission,
  hasUnconfirmedTerms,
  portalIdIsInAllowlist,
  children
}) => {
  const authorizationData = useSelector(getApplicationAuthorizationData);
  const appName = authorizationData === null || authorizationData === void 0 ? void 0 : authorizationData.get('name');
  const getTooltipMessage = () => {
    if (hasNoInstallPermission) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.app.marketplaceAccess.needPermissionTooltip"
      });
    }
    if (!portalIdIsInAllowlist) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "developer-experience-shared-components.accountPicker.table.disabledTooltip",
        options: {
          appName
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "oauth.app.termsAndPrivacy.tooltip"
    });
  };
  return /*#__PURE__*/_jsx(Tooltip, {
    title: getTooltipMessage(),
    disabled: !hasNoInstallPermission && !hasUnconfirmedTerms && portalIdIsInAllowlist,
    children: children
  });
};
const ConnectAppButton = ({
  hasNoInstallPermission,
  hasUnconfirmedTerms
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    allowParentRedirect,
    sendMessage
  } = useContext(EmbeddedContext);
  const {
    data: account
  } = useFetchCurrentAccount();
  const authorizeStatus = useSelector(getAuthorizeStatus);
  const isRequestingNewScopes = useSelector(getIsRequestingNewScopes);
  const portalIdIsInAllowlist = !(account !== null && account !== void 0 && account.disabled);
  const recaptchaKey = V3_SITE_KEYS[enviro.getShort()];
  const {
    executeRecaptcha
  } = useRecaptchav3({
    reCaptchaKey: recaptchaKey
  });
  const authorizeApp = data => {
    dispatch(authorize({
      data,
      trackerOptions: {
        hasInstallPermission: !hasNoInstallPermission,
        isRequestingNewScopes: !!isRequestingNewScopes
      },
      redirectOptions: {
        allowParentRedirect,
        sendMessage
      }
    })).unwrap().catch(e => {
      Raven.captureException(e);
    });
  };
  const onConnectAppClicked = () => {
    executeRecaptcha().then(token => authorizeApp(getAuthorizationRequestInfo(location.search, token))).catch(e => Raven.captureException(e));
  };
  return /*#__PURE__*/_jsx(DisabledButtonTooltip, {
    hasNoInstallPermission: hasNoInstallPermission,
    hasUnconfirmedTerms: hasUnconfirmedTerms,
    portalIdIsInAllowlist: portalIdIsInAllowlist,
    children: /*#__PURE__*/_jsx(LoadingButton, {
      use: "primary",
      loading: authorizeStatus.pending,
      failed: authorizeStatus.failed,
      onClick: onConnectAppClicked,
      "data-test-id": "authorize-button",
      disabled: hasNoInstallPermission || !executeRecaptcha || hasUnconfirmedTerms || !portalIdIsInAllowlist,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "oauth.authorization.connectApp"
      })
    })
  });
};
export default ConnectAppButton;